import { useEffect, useRef, useState, type Dispatch, type ReactNode, type SetStateAction } from 'react'
import { useTranslation } from 'react-i18next'
import { Icon } from '@iconify/react'
import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Chip from '@mui/material/Chip'
import InputAdornment from '@mui/material/InputAdornment'
import Slider from '@mui/material/Slider'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import FormControlLabel from '@mui/material/FormControlLabel'
import Switch from '@mui/material/Switch'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import type { TFunction } from 'i18next'
import { ConfirmDialog, IconButton, TimeField } from '@jupi/ui'
import {
  DEFAULT_FLIGHT_DURATION_WARNING_HOURS,
  DEFAULT_TAXI_WARNING_MINUTES,
  FUEL_UNITS,
  LITRES_PER_USG,
  fuelChain,
  type FuelState,
  OIL_UNITS,
  durationDigitsToMinutes,
  flightClockTimeline,
  flightClockWarnings,
  formatDurationDigits,
  fromLitres,
  formatMinutes,
  localizedFlightTypeLabel,
  minutesToDurationDigits,
  nextDigits,
  quickTimeGroups,
  resolveFlightClock,
  toLitres,
  type FlightLogAircraft,
  type FlightLogConfig,
  type FlightType,
  type FuelUnit,
  type OilUnit,
} from '@jupi/shared'
import AerodromeField, { AERODROME_CODE } from './AerodromeField'
import { FlightLogTimeline } from './FlightLogCard'
import { TimeBaseToggle, useTimeBase } from './timeBase'

// The fields a flight is logged with, shared by the flight log's entry form
// and the flight school's: the aircraft and flight type, the times (typed in
// one go or one by one, drawn live as a timeline), the airfields, the
// landings and people aboard, night and IFR time, and fuel and oil. A page
// owns its form state (a FlightFormState, plus whatever else it asks) and
// the save; these draw and edit the shared part of it. Each page then adds
// its own: the flight log, crew and the airframe total; the school, the
// lesson and its checklist.

const LAST_AIRCRAFT_KEY = 'jupi.flightLog.lastAircraft'
const QUICK_TIMES_KEY = 'jupi.flightLog.quickTimes'
export const readLastAircraft = () => {
  try {
    return localStorage.getItem(LAST_AIRCRAFT_KEY)
  } catch {
    return null
  }
}
// Quick entry is the default on a phone (below MUI's md breakpoint, where
// the layout itself turns mobile), separate fields on a desktop -- until
// the member flips the switch, which is then remembered on that device.
const isPhoneWidth = () => typeof window !== 'undefined' && window.matchMedia('(max-width: 899.95px)').matches

const readQuickTimes = () => {
  try {
    const stored = localStorage.getItem(QUICK_TIMES_KEY)
    return stored === null ? isPhoneWidth() : stored === 'true'
  } catch {
    return isPhoneWidth()
  }
}
export const rememberQuickTimes = (on: boolean) => {
  try {
    localStorage.setItem(QUICK_TIMES_KEY, String(on))
  } catch {
    // Private browsing: it lasts until the page is reloaded.
  }
}

export const rememberAircraft = (registration: string) => {
  try {
    localStorage.setItem(LAST_AIRCRAFT_KEY, registration)
  } catch {
    // Private browsing: the default is merely less convenient.
  }
}

// "40,5" or "40.5" -> 40.5; empty -> null.
export const litres = (value: string) => {
  const trimmed = value.trim().replace(',', '.')
  if (!trimmed) return null
  const n = Number(trimmed)
  return Number.isFinite(n) ? n : Number.NaN
}

// null: not entered; NaN: not a number (blocks the save).
const amountInLitres = (value: string, unit: FuelUnit | OilUnit) => {
  const n = litres(value)
  return n === null || Number.isNaN(n) ? n : toLitres(n, unit)
}

// The unit last picked, remembered per browser.
const FUEL_UNIT_KEY = 'jupi.flightLog.fuelUnit'
const OIL_UNIT_KEY = 'jupi.flightLog.oilUnit'
function readUnit<U extends string>(key: string, allowed: readonly U[], fallback: U): U {
  try {
    const stored = localStorage.getItem(key)
    return allowed.find((u) => u === stored) ?? fallback
  } catch {
    return fallback
  }
}
const rememberUnit = (key: string, unit: string) => {
  try {
    localStorage.setItem(key, unit)
  } catch {
    // Remembering the unit is a convenience.
  }
}

// YYYY-MM-DD of an instant in `timeZone`.
export const dateIn = (ms: number, timeZone: string) => new Intl.DateTimeFormat('en-CA', { timeZone }).format(ms)

// Minutes between two times on the flight's timeline, once both are in.
export const between = (from: number | null, to: number | null) => (from !== null && to !== null ? to - from : null)

function useZoneSuffix() {
  const { t } = useTranslation()
  const { timeBase } = useTimeBase()
  return timeBase === 'utc' ? 'UTC' : t('flightLog.localShort')
}

export interface FlightFormState {
  registration: string
  flightType: string
  date: string
  offBlockTime: string
  takeoffTime: string
  landingTime: string
  onBlockTime: string
  departure: string
  arrival: string
  landings: number
  personsOnBoard: number
  // Night and IFR time as the digits typed: "120" is 1:20.
  nightDigits: string
  nightLandings: number
  ifrDigits: string
  // As typed, in `fuelUnit` / `oilUnit`; the model converts to litres.
  fuelAdded: string
  // The total in the tanks after refuelling, as typed in `fuelUnit`.
  fuelMeasured: string
  fuelUnit: FuelUnit
  oilAdded: string
  oilUnit: OilUnit
  // Litres, or null for not recorded.
  fuelRemainingLitres: number | null
  // The fuel the flight started with when editing a saved one; null: the aircraft's current fuel.
  fuelStart: { litres: number | null } | null
  remarks: string
}

export const emptyFlightForm = (today: string): FlightFormState => ({
  registration: '',
  flightType: '',
  date: today,
  offBlockTime: '',
  takeoffTime: '',
  landingTime: '',
  onBlockTime: '',
  departure: '',
  arrival: '',
  landings: 1,
  personsOnBoard: 1,
  nightDigits: '',
  nightLandings: 0,
  ifrDigits: '',
  fuelAdded: '',
  fuelMeasured: '',
  fuelUnit: readUnit(FUEL_UNIT_KEY, FUEL_UNITS, 'l'),
  oilAdded: '',
  oilUnit: readUnit(OIL_UNIT_KEY, OIL_UNITS, 'l'),
  fuelRemainingLitres: null,
  fuelStart: null,
  remarks: '',
})

// What the form's values add up to, recomputed on every change: the clock
// (flight and block time), where the times are out of order or past
// midnight, the admin's "too long" warnings, the parsed fuel, oil, night and
// IFR figures, and the names of the fields that stop a save.
export function useFlightModel(form: FlightFormState, config: FlightLogConfig | undefined, aircraft: FlightLogAircraft[]) {
  const clockInput = {
    offBlock: form.offBlockTime,
    takeoff: form.takeoffTime,
    landing: form.landingTime,
    onBlock: form.onBlockTime,
  }
  const clock = resolveFlightClock(clockInput)
  // The times as entered so far: which are earlier than the one before
  // them, shown in red as they are typed, and which rolled past midnight.
  const timeline = flightClockTimeline(clockInput)
  const nextDay = timeline.dayOffsets.map((d, i) => d > 0 && timeline.minutes[i] !== null)
  const warningLevels = {
    flightDurationWarningHours: config?.flightDurationWarningHours ?? DEFAULT_FLIGHT_DURATION_WARNING_HOURS,
    taxiWarningMinutes: config?.taxiWarningMinutes ?? DEFAULT_TAXI_WARNING_MINUTES,
  }
  const flightMinutes = clock?.flightMinutes ?? between(timeline.minutes[1], timeline.minutes[2])
  const blockMinutes = clock?.blockMinutes ?? between(timeline.minutes[0], timeline.minutes[3])
  const nightMinutes = durationDigitsToMinutes(form.nightDigits)
  const ifrMinutes = durationDigitsToMinutes(form.ifrDigits)
  // Why a night or IFR time will not do, as a message key; null when fine.
  const durationIssue = (minutes: number | null) =>
    minutes == null
      ? 'flightLog.durationMinutesInvalid'
      : flightMinutes != null && minutes > flightMinutes
        ? 'flightLog.durationTooLong'
        : null
  const fuel = amountInLitres(form.fuelAdded, form.fuelUnit)
  const oil = amountInLitres(form.oilAdded, form.oilUnit)
  const measured = amountInLitres(form.fuelMeasured, form.fuelUnit)
  const measuredIssue = measured != null && !Number.isNaN(measured) && fuel != null && !Number.isNaN(fuel) && measured < fuel
  const selected = aircraft.find((a) => a.registration === form.registration)
  const startLitres = form.fuelStart ? form.fuelStart.litres : (selected?.fuelAtStartLitres ?? null)
  const fuelState =
    selected && flightMinutes != null && blockMinutes != null
      ? fuelChain(
          [
            {
              flightId: 'form',
              departure: '',
              arrival: '',
              flightMinutes,
              blockMinutes,
              addedLitres: fuel != null && !Number.isNaN(fuel) ? fuel : null,
              measuredLitres: measured != null && !Number.isNaN(measured) ? measured : null,
              reportedRemainingLitres: form.fuelRemainingLitres,
            },
          ],
          {
            burnLitresPerHour: selected.fuelBurnLitresPerHour,
            taxiLitresPerTenMin: selected.fuelTaxiLitresPerTenMin,
            usableLitres: selected.usableFuelLitres,
          },
          startLitres,
        ).get('form')!
      : null
  const nightIssue = durationIssue(nightMinutes)
  const ifrIssue = durationIssue(ifrMinutes)

  const issues: string[] = []
  if (!form.registration) issues.push('registration')
  if (!form.flightType) issues.push('flightType')
  if (!clock) issues.push('times')
  if (!AERODROME_CODE.test(form.departure) || !AERODROME_CODE.test(form.arrival)) issues.push('airfields')
  if (Number.isNaN(fuel) || Number.isNaN(oil) || Number.isNaN(measured) || measuredIssue) issues.push('fuel')
  if (nightIssue || ifrIssue || form.nightLandings > form.landings) issues.push('nightIfr')

  return {
    clock,
    outOfOrder: timeline.wrong,
    nextDay,
    warnings: flightClockWarnings(clockInput, warningLevels),
    warningLevels,
    flightMinutes,
    blockMinutes,
    fuel,
    oil,
    measured,
    measuredIssue,
    fuelState,
    nightMinutes,
    ifrMinutes,
    nightIssue,
    ifrIssue,
    selected,
    issues,
  }
}
export type FlightModel = ReturnType<typeof useFlightModel>

export interface FlightFieldsProps<F extends FlightFormState> {
  form: F
  set: <K extends keyof FlightFormState>(key: K, value: FlightFormState[K]) => void
  setForm: Dispatch<SetStateAction<F>>
  model: FlightModel
  aircraft: FlightLogAircraft[]
  flightTypes: Pick<FlightType, 'name' | 'label' | 'labelSv' | 'labelEn'>[]
  /** Today, in whichever clock the member is logging in. */
  today: string
  /** The label of a flight type that is no longer selectable, on a flight that has it. */
  retiredTypeLabel?: string
  /** The airfield a new flight starts from and returns to, until the member picks one. Omit when correcting a flight. */
  defaultAirfield?: string | null
  /** The fewest people aboard: a dual school flight has two. */
  minPersons?: number
  /** Heading of the section holding the aircraft and flight type. */
  aircraftTitle?: string
  /** Issue codes (FlightModel.issues) to outline, once the member has tried to save. */
  highlight?: string[]
}

// The aircraft, the flight type, the times and airfields, the landings and
// the people aboard.
export function FlightFieldsMain<F extends FlightFormState>({
  form,
  set,
  setForm,
  model,
  aircraft,
  flightTypes,
  today,
  retiredTypeLabel,
  defaultAirfield,
  minPersons = 1,
  aircraftTitle,
  highlight = [],
}: FlightFieldsProps<F>) {
  const { t, i18n } = useTranslation()
  const { selected, outOfOrder, nextDay, warnings, warningLevels, clock, flightMinutes, blockMinutes } = model
  const [quickTimes, setQuickTimes] = useState(readQuickTimes)
  const [airfieldsTouched, setAirfieldsTouched] = useState(false)
  const zoneSuffix = useZoneSuffix()
  const arrival = form.arrival

  useEffect(() => {
    if (!defaultAirfield || airfieldsTouched) return
    setForm((f) => ({ ...f, departure: defaultAirfield, arrival: defaultAirfield }))
  }, [defaultAirfield, airfieldsTouched, setForm])

  return (
    <>
      <Section title={aircraftTitle ?? t('booking.aircraft')} error={highlight.includes('registration') || highlight.includes('flightType')}>
        <ChipChoice
          options={aircraft.map((a) => ({
            value: a.registration,
            label: a.registration,
            disabled: !a.current,
          }))}
          value={form.registration}
          onChange={(v) => set('registration', v)}
        />
        {selected && !selected.current ? (
          <Typography variant="caption" color="text.secondary">
            {t('flightLog.noJourneyLog')}
          </Typography>
        ) : null}
        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
          {t('flightLog.flightType')}
        </Typography>
        <ChipChoice
          options={flightTypes.map((ft) => ({ value: ft.name, label: localizedFlightTypeLabel(ft, i18n.language) }))}
          value={form.flightType}
          onChange={(v) => set('flightType', v)}
        />
        {retiredTypeLabel && !flightTypes.some((ft) => ft.name === form.flightType) ? (
          <Typography variant="caption" color="text.secondary">
            {retiredTypeLabel}
          </Typography>
        ) : null}
      </Section>

      <Section title={t('flightLog.times')} error={highlight.includes('times') || highlight.includes('airfields')}>
        <Box sx={{ mb: 2 }}>
          <TimeBaseToggle />
        </Box>
        <DateChoice value={form.date} today={today} onChange={(v) => set('date', v)} />
        <FormControlLabel
          sx={{ mt: 1.5 }}
          control={
            <Switch
              checked={quickTimes}
              onChange={(e) => {
                setQuickTimes(e.target.checked)
                rememberQuickTimes(e.target.checked)
              }}
            />
          }
          label={t('flightLog.quickEntry')}
        />
        {quickTimes ? (
          <QuickTimes
            times={[form.offBlockTime, form.takeoffTime, form.landingTime, form.onBlockTime]}
            outOfOrder={outOfOrder}
            onChange={([offBlockTime, takeoffTime, landingTime, onBlockTime]) =>
              setForm((f) => ({ ...f, offBlockTime, takeoffTime, landingTime, onBlockTime }))
            }
          />
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mt: 1 }}>
            {TIME_FIELDS.map(([key, icon], i) => (
              <TimeField
                key={key}
                label={t(`flightLog.${key}`)}
                value={form[key]}
                error={outOfOrder[i]}
                helperText={
                  outOfOrder[i]
                    ? t(orderMessage(i), { field: t(`flightLog.${key}`), time: form[key] })
                    : nextDay[i]
                      ? t('flightLog.nextDay')
                      : undefined
                }
                onChange={(v) => set(key, v)}
                slotProps={{
                  htmlInput: { style: { fontSize: '1.2rem' } },
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Icon icon={icon} width={20} />
                      </InputAdornment>
                    ),
                  },
                }}
              />
            ))}
          </Box>
        )}
        <Stack direction="row" spacing={1} sx={{ mt: 2, alignItems: 'center' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <AerodromeField
            label={t('flightLog.departure')}
            value={form.departure}
            onChange={(code) => {
              setAirfieldsTouched(true)
              set('departure', code)
            }}
          />
        </Box>
        <IconButton
          aria-label={t('flightLog.swapAirfields')}
          onClick={() => {
            setAirfieldsTouched(true)
            setForm((f) => ({ ...f, departure: f.arrival, arrival: f.departure }))
          }}
        >
          <Icon icon="mdi:swap-horizontal" />
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <AerodromeField
            label={t('flightLog.arrival')}
            value={arrival}
            onChange={(code) => {
              setAirfieldsTouched(true)
              set('arrival', code)
            }}
          />
        </Box>
      </Stack>

        {/* The flight's line in the log, live. */}
        <Box sx={{ mt: 2, p: 1.5, borderRadius: 1, bgcolor: 'action.hover' }}>
          <FlightLogTimeline
            departure={form.departure}
            arrival={arrival}
            offBlock={form.offBlockTime}
            takeoff={form.takeoffTime}
            landing={form.landingTime}
            onBlock={form.onBlockTime}
            nextDay={nextDay}
            flightMinutes={flightMinutes}
            blockMinutes={blockMinutes}
            zoneLabel={zoneSuffix}
          />
        </Box>
        {/* Sanity checks the admin sets the levels for: warnings, so a
            genuinely long flight still saves. */}
        {clock && warnings.longFlight ? (
          <Alert severity="warning" sx={{ mt: 1 }}>
            {t('flightLog.longFlightWarning', {
              time: formatMinutes(clock.flightMinutes),
              hours: warningLevels.flightDurationWarningHours,
            })}
          </Alert>
        ) : null}
        {clock && warnings.longBlock ? (
          <Alert severity="warning" sx={{ mt: 1 }}>
            {t('flightLog.longBlockWarning', {
              time: formatMinutes(clock.blockMinutes),
              hours: warningLevels.flightDurationWarningHours,
            })}
          </Alert>
        ) : null}
        {clock && warnings.longTaxi ? (
          <Alert severity="warning" sx={{ mt: 1 }}>
            {t('flightLog.longTaxiWarning', {
              minutes: clock.taxiMinutes,
              limit: warningLevels.taxiWarningMinutes,
            })}
          </Alert>
        ) : null}
      </Section>

      <Section title={t('flightLog.landingsAndPeople')}>
        <Stack direction="row" spacing={2} sx={{ justifyContent: 'space-around' }}>
          <Stepper
            label={t('flightLog.landings')}
            icon="mdi:airplane-landing"
            value={form.landings}
            min={1}
            max={99}
            onChange={(v) => set('landings', v)}
          />
          <Stepper
            label={t('flightLog.personsOnBoard')}
            icon="mdi:account-multiple"
            value={form.personsOnBoard}
            min={minPersons}
            max={selected?.seats ?? 20}
            onChange={(v) => set('personsOnBoard', v)}
          />
        </Stack>
      </Section>
    </>
  )
}

// Night and IFR time, then fuel and oil: folded away until wanted. Fuel and
// oil are asked for softly: "none added" is a button, and the page confirms
// before saving with neither entered.
export function FlightFieldsExtras<F extends FlightFormState>({
  form,
  set,
  model,
  openFuelSignal,
}: Pick<FlightFieldsProps<F>, 'form' | 'set' | 'model'> & { openFuelSignal?: number }) {
  const { t } = useTranslation()
  const { selected, flightMinutes, nightMinutes, ifrMinutes, nightIssue, ifrIssue, fuel, oil, measured, measuredIssue, fuelState } = model
  const [nightOpen, setNightOpen] = useState(Boolean(form.nightDigits || form.ifrDigits || form.nightLandings))
  const [fuelOpen, setFuelOpen] = useState(
    Boolean(form.fuelAdded || form.fuelMeasured || form.oilAdded || form.fuelRemainingLitres != null),
  )
  // The save asked the pilot to fill fuel and oil in: unfold them.
  useEffect(() => {
    if (openFuelSignal) setFuelOpen(true)
  }, [openFuelSignal])
  return (
    <>
      <Accordion disableGutters variant="outlined" expanded={nightOpen} onChange={(_, open) => setNightOpen(open)}>
        <AccordionSummary expandIcon={<Icon icon="mdi:chevron-down" />}>
          <Typography sx={{ fontWeight: 600 }}>{t('flightLog.nightAndIfr')}</Typography>
          <Typography color="text.secondary" sx={{ ml: 1 }}>
            {[
              nightMinutes ? `${t('flightLog.night')} ${formatMinutes(nightMinutes)}` : '',
              form.nightLandings ? `${form.nightLandings} ${t('flightLog.nightLandingsShort')}` : '',
              ifrMinutes ? `IFR ${formatMinutes(ifrMinutes)}` : '',
            ]
              .filter(Boolean)
              .join(' · ') || t('flightLog.optional')}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <DurationField
              label={t('flightLog.nightTime')}
              icon="mdi:weather-night"
              digits={form.nightDigits}
              error={nightIssue}
              flightMinutes={flightMinutes}
              onChange={(v) => set('nightDigits', v)}
            />
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Stepper
                label={t('flightLog.nightLandings')}
                icon="mdi:weather-night"
                value={form.nightLandings}
                min={0}
                max={form.landings}
                onChange={(v) => set('nightLandings', v)}
              />
            </Box>
            <DurationField
              label={t('flightLog.ifrTime')}
              icon="mdi:weather-fog"
              digits={form.ifrDigits}
              error={ifrIssue}
              flightMinutes={flightMinutes}
              onChange={(v) => set('ifrDigits', v)}
            />
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Accordion disableGutters variant="outlined" expanded={fuelOpen} onChange={(_, open) => setFuelOpen(open)}>
        <AccordionSummary expandIcon={<Icon icon="mdi:chevron-down" />}>
          <Typography sx={{ fontWeight: 600 }}>{t('flightLog.fuelAndOil')}</Typography>
          <Typography color="text.secondary" sx={{ ml: 1 }}>
            {fuelOilSummary(form, model, t)}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack direction="row" spacing={2}>
            <AmountField
              label={t('flightLog.fuelAdded')}
              value={form.fuelAdded}
              unit={form.fuelUnit}
              units={FUEL_UNITS}
              error={Number.isNaN(fuel)}
              onChange={(v) => set('fuelAdded', v)}
              onUnit={(u) => {
                rememberUnit(FUEL_UNIT_KEY, u)
                set('fuelUnit', u)
                set('fuelAdded', convertTyped(form.fuelAdded, form.fuelUnit, u))
                set('fuelMeasured', convertTyped(form.fuelMeasured, form.fuelUnit, u))
              }}
            />
            <AmountField
              label={t('flightLog.oilAdded')}
              value={form.oilAdded}
              unit={form.oilUnit}
              units={OIL_UNITS}
              error={Number.isNaN(oil)}
              onChange={(v) => set('oilAdded', v)}
              onUnit={(u) => {
                rememberUnit(OIL_UNIT_KEY, u)
                set('oilUnit', u)
                set('oilAdded', convertTyped(form.oilAdded, form.oilUnit, u))
              }}
            />
          </Stack>
          <TextField
            fullWidth
            sx={{ mt: 2 }}
            label={t('flightLog.fuelMeasured')}
            helperText={measuredIssue ? t('flightLog.fuelMeasuredBelowAdded') : t('flightLog.fuelMeasuredHint')}
            value={form.fuelMeasured}
            onChange={(e) => set('fuelMeasured', e.target.value)}
            error={Number.isNaN(measured) || measuredIssue}
            slotProps={{
              htmlInput: { inputMode: 'decimal' },
              input: { endAdornment: <InputAdornment position="end">{UNIT_LABEL[form.fuelUnit]}</InputAdornment> },
            }}
          />
          <Button
            size="small"
            sx={{ mt: 1 }}
            startIcon={<Icon icon="mdi:check" />}
            disabled={Boolean(form.fuelAdded.trim() && form.oilAdded.trim())}
            onClick={() => {
              if (!form.fuelAdded.trim()) set('fuelAdded', '0')
              if (!form.oilAdded.trim()) set('oilAdded', '0')
            }}
          >
            {t('flightLog.noFuelOilAdded')}
          </Button>
          <FuelRemaining
            usableLitres={selected?.usableFuelLitres ?? null}
            state={fuelState}
            value={form.fuelRemainingLitres}
            onChange={(v) => set('fuelRemainingLitres', v)}
          />
        </AccordionDetails>
      </Accordion>
    </>
  )
}

const UNIT_LABEL = { l: 'l', usg: 'USG', qt: 'qt' } as const

// A typed amount re-expressed in another unit ("" stays empty).
function convertTyped(value: string, from: FuelUnit | OilUnit, to: FuelUnit | OilUnit): string {
  const n = litres(value)
  if (n === null || Number.isNaN(n)) return value
  return String(fromLitres(toLitres(n, from), to))
}

function fuelOilSummary(form: FlightFormState, model: { fuel: number | null; oil: number | null }, t: TFunction) {
  if (model.fuel === 0 && model.oil === 0 && form.fuelRemainingLitres == null) return t('flightLog.noFuelOilAdded')
  return (
    [
      form.fuelAdded && `+${form.fuelAdded} ${UNIT_LABEL[form.fuelUnit]}`,
      form.oilAdded && `${t('flightLog.oil')} ${form.oilAdded} ${UNIT_LABEL[form.oilUnit]}`,
      form.fuelRemainingLitres != null && `${t('flightLog.remainingShort')} ${form.fuelRemainingLitres} l`,
    ]
      .filter(Boolean)
      .join(' · ') || t('flightLog.optional')
  )
}

function AmountField<U extends FuelUnit | OilUnit>({
  label,
  value,
  unit,
  units,
  error,
  onChange,
  onUnit,
}: {
  label: string
  value: string
  unit: U
  units: readonly U[]
  error: boolean
  onChange: (value: string) => void
  onUnit: (unit: U) => void
}) {
  return (
    <Stack spacing={1} sx={{ flex: 1 }}>
      <TextField
        fullWidth
        label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        error={error}
        slotProps={{
          htmlInput: { inputMode: 'decimal' },
          input: { endAdornment: <InputAdornment position="end">{UNIT_LABEL[unit]}</InputAdornment> },
        }}
      />
      <ToggleButtonGroup exclusive size="small" value={unit} onChange={(_, u: U | null) => u && onUnit(u)} aria-label={label}>
        {units.map((u) => (
          <ToggleButton key={u} value={u} sx={{ px: 1.5, py: 0.25 }}>
            {UNIT_LABEL[u]}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Stack>
  )
}

// Asks the pilot to confirm that neither fuel nor oil (or one of them) was
// added, when the form is saved with the field left empty.
export function FuelOilConfirm({
  missing,
  onConfirm,
  onBack,
}: {
  missing: ('fuel' | 'oil')[]
  onConfirm: () => void
  onBack: () => void
}) {
  const { t } = useTranslation()
  return (
    <ConfirmDialog
      open={missing.length > 0}
      destructive={false}
      title={t('flightLog.fuelOilConfirmTitle')}
      message={t(`flightLog.fuelOilConfirm_${missing.length === 2 ? 'both' : (missing[0] ?? 'both')}`)}
      confirmLabel={t('flightLog.fuelOilConfirmSave')}
      cancelLabel={t('flightLog.fuelOilConfirmBack')}
      onConfirm={onConfirm}
      onClose={onBack}
    />
  )
}

export function Section({ title, children, error }: { title: string; children: ReactNode; error?: boolean }) {
  return (
    <Card variant="outlined" sx={error ? { borderColor: 'error.main', borderWidth: 2 } : undefined}>
      <CardContent>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {title}
        </Typography>
        {children}
      </CardContent>
    </Card>
  )
}

// One tap per choice: a wrapping row of chips rather than a dropdown.
export function ChipChoice({
  options,
  value,
  onChange,
}: {
  options: { value: string; label: string; disabled?: boolean }[]
  value: string
  onChange: (value: string) => void
}) {
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      {options.map((o) => (
        <Chip
          key={o.value}
          label={o.label}
          disabled={o.disabled}
          color={o.value === value ? 'primary' : 'default'}
          variant={o.value === value ? 'filled' : 'outlined'}
          onClick={() => onChange(o.value)}
          sx={{ height: 40, fontSize: '0.95rem', px: 0.5 }}
        />
      ))}
    </Box>
  )
}

// Night or IFR time, typed on the number pad (which has no colon): the
// last two digits are minutes, and the box formats itself as h:mm -- 15 is
// 0:15, 120 is 1:20. "Whole flight" fills in the flight time, for a flight
// flown entirely at night or on instruments.
export function DurationField({
  label,
  icon,
  digits,
  error,
  flightMinutes,
  onChange,
}: {
  label: string
  icon: string
  digits: string
  error: string | null
  flightMinutes: number | null
  onChange: (digits: string) => void
}) {
  const { t } = useTranslation()
  const text = formatDurationDigits(digits)
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
      <TextField
        fullWidth
        label={label}
        placeholder="0:00"
        value={text}
        error={Boolean(error)}
        helperText={error ? t(error) : t('flightLog.durationHelp')}
        // The "0:" a short entry is shown with is formatting, not digits.
        onChange={(e) => onChange(nextDigits(digits, text, e.target.value, 6).replace(/^0+/, '').slice(0, 4))}
        slotProps={{
          htmlInput: { inputMode: 'numeric', pattern: '[0-9]*', autoComplete: 'off' },
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <Icon icon={icon} width={20} />
              </InputAdornment>
            ),
          },
        }}
      />
      <Button
        variant="outlined"
        sx={{ minHeight: 56, whiteSpace: 'nowrap' }}
        disabled={flightMinutes == null}
        onClick={() => flightMinutes != null && onChange(minutesToDurationDigits(flightMinutes))}
      >
        {t('flightLog.wholeFlight')}
      </Button>
    </Stack>
  )
}

export const TIME_FIELDS = [
  ['offBlockTime', 'mdi:account-tie-hat'],
  ['takeoffTime', 'mdi:airplane-takeoff'],
  ['landingTime', 'mdi:airplane-landing'],
  ['onBlockTime', 'mdi:car-brake-parking'],
] as const

// Why time `i` is out of order, as a message key: landing must be after
// takeoff; every other time the same as or later than the one before.
export const orderMessage = (i: number) => (i === 2 ? 'flightLog.landingBeforeTakeoff' : 'flightLog.timeBeforePrevious')

// The four times in one box, typed straight through on the number pad or
// keyboard -- HHMM each, in the order they happen -- with no tapping from
// field to field. Each time is formatted as it fills; the timeline below
// draws them as they complete.
export function QuickTimes({
  times,
  outOfOrder,
  onChange,
}: {
  times: [string, string, string, string]
  outOfOrder: boolean[]
  onChange: (times: [string, string, string, string]) => void
}) {
  const { t } = useTranslation()
  // Seeded from times already entered in the separate fields, up to the
  // first gap.
  const [digits, setDigits] = useState(() => {
    let seed = ''
    for (const time of times) {
      if (!time) break
      seed += time.replace(':', '')
    }
    return seed
  })
  const groups = quickTimeGroups(digits)
  const text = groups.map((g) => g.text).join('  ')
  const invalid = groups.find((g) => g.complete && !g.time)
  const wrong = outOfOrder.findIndex(Boolean)
  const next = groups.length === 0 ? 0 : groups.at(-1)!.complete ? groups.length : groups.length - 1

  // The order of the times as the label, the one being typed in bold and
  // any that is not a time or is out of order in red.
  const label = (
    <span>
      {TIME_FIELDS.map(([key], i) => (
        <span key={key}>
          {i > 0 ? ' · ' : ''}
          <Box
            component="span"
            sx={{
              fontWeight: i === next ? 700 : undefined,
              color:
                outOfOrder[i] || (groups[i]?.complete && !groups[i]?.time)
                  ? 'error.main'
                  : i === next
                    ? 'text.primary'
                    : 'text.secondary',
            }}
          >
            {t(`flightLog.${key}`)}
          </Box>
        </span>
      ))}
    </span>
  )

  const update = (value: string) => {
    const nextValue = nextDigits(digits, text, value, 16)
    setDigits(nextValue)
    const parsed = quickTimeGroups(nextValue)
    onChange([0, 1, 2, 3].map((i) => parsed[i]?.time ?? '') as [string, string, string, string])
  }

  return (
    <Box sx={{ mt: 1 }}>
      <TextField
        fullWidth
        autoFocus
        label={label}
        placeholder="HH:MM  HH:MM  HH:MM  HH:MM"
        value={text}
        error={Boolean(invalid) || wrong >= 0}
        helperText={
          invalid
            ? t('flightLog.quickEntryInvalid', { time: invalid.text })
            : wrong >= 0
              ? t(orderMessage(wrong), { field: t(`flightLog.${TIME_FIELDS[wrong]![0]}`), time: times[wrong] })
              : next < 4
              ? t('flightLog.quickEntryNext', { field: t(`flightLog.${TIME_FIELDS[next]![0]}`) })
              : t('flightLog.quickEntryDone')
        }
        onChange={(e) => update(e.target.value)}
        slotProps={{
          htmlInput: {
            inputMode: 'numeric',
            pattern: '[0-9]*',
            autoComplete: 'off',
            style: { fontSize: '1.3rem', letterSpacing: '0.05em', fontVariantNumeric: 'tabular-nums' },
          },
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <Icon
                  icon={wrong >= 0 || invalid ? 'mdi:alert-circle-outline' : next < 4 ? TIME_FIELDS[next]![1] : 'mdi:check'}
                  width={22}
                />
              </InputAdornment>
            ),
          },
        }}
      />
    </Box>
  )
}

// The flight's date: "Today" by default, a calendar beside it for any
// other day, and the chosen date in place of "Today" once it is not --
// clearable back to today.
export function DateChoice({ value, today, onChange }: { value: string; today: string; onChange: (date: string) => void }) {
  const { t, i18n } = useTranslation()
  const input = useRef<HTMLInputElement>(null)
  const isToday = value === today
  const label = new Intl.DateTimeFormat(i18n.language, {
    timeZone: 'UTC',
    weekday: 'short',
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
  }).format(new Date(`${value}T12:00:00Z`))

  const openPicker = () => {
    try {
      input.current?.showPicker()
    } catch {
      input.current?.focus()
    }
  }

  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
      {isToday ? (
        <Button variant="contained" size="large" startIcon={<Icon icon="mdi:calendar-today" />} sx={{ minHeight: 48 }}>
          {t('flightLog.today')}
        </Button>
      ) : (
        <Chip
          color="primary"
          label={label}
          onClick={openPicker}
          onDelete={() => onChange(today)}
          aria-label={`${label}, ${t('flightLog.backToToday')}`}
          sx={{ height: 48, fontSize: '1rem', px: 1 }}
        />
      )}
      {/* The native date input sits on top of the calendar button, invisible,
          so a tap opens the phone's own picker even where showPicker() is
          missing. */}
      <Box sx={{ position: 'relative', width: 48, height: 48 }}>
        <IconButton aria-hidden tabIndex={-1} sx={{ width: 48, height: 48, border: 1, borderColor: 'divider' }}>
          <Icon icon="mdi:calendar-month" />
        </IconButton>
        <Box
          component="input"
          ref={input}
          type="date"
          aria-label={t('flightLog.pickDate')}
          value={value}
          max={today}
          onClick={openPicker}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => e.target.value && onChange(e.target.value)}
          sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer', border: 0 }}
        />
      </Box>
    </Stack>
  )
}

// What the gauges read after shutdown, as mik-ng has it: a slider over the
// aircraft's usable fuel with marks every 10 %, and litre and US gallon
// boxes kept in step for a precise figure. Optional: "not recorded" until
// touched, and clearable.
const FALLBACK_TANK_LITRES = 200
const fuelLabel = (l: number) => `${Math.round(l)} l = ${Math.round(l / LITRES_PER_USG)} USG`

export function FuelRemaining({
  usableLitres,
  state,
  value,
  onChange,
}: {
  usableLitres: number | null
  // What the fuel chain makes of it; shown while the pilot has not read the gauge.
  state: FuelState | null
  value: number | null
  onChange: (value: number | null) => void
}) {
  const { t } = useTranslation()
  const max = usableLitres ?? FALLBACK_TANK_LITRES
  const clamp = (n: number) => Math.min(max, Math.max(0, Math.round(n)))
  const marks = [0.1, 0.3, 0.5, 0.7, 0.9].map((share) => ({ value: max * share, label: `${share * 100}%` }))
  return (
    <Box sx={{ mt: 3 }}>
      <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
        <Typography variant="body2" id="fuel-remaining-label">
          {t('flightLog.fuelRemaining')}
        </Typography>
        {value != null ? (
          <Button size="small" onClick={() => onChange(null)}>
            {t('flightLog.clear')}
          </Button>
        ) : null}
      </Stack>
      {value == null && state?.remainingLitres != null ? (
        <Alert severity={state.uncertain ? 'warning' : 'info'} sx={{ mb: 2 }}>
          {t(state.basis === 'measured' ? 'flightLog.fuelFromMeasured' : 'flightLog.fuelFromEstimate', {
            litres: state.remainingLitres,
          })}
          {state.uncertain ? ` ${t('flightLog.fuelUncertain')}` : ''}
        </Alert>
      ) : null}
      <Box sx={{ px: 1.5 }}>
        <Slider
          value={value ?? 0}
          min={0}
          max={max}
          step={LITRES_PER_USG}
          marks={marks}
          valueLabelDisplay={value != null ? 'on' : 'off'}
          valueLabelFormat={fuelLabel}
          getAriaValueText={fuelLabel}
          aria-labelledby="fuel-remaining-label"
          onChange={(_, v) => onChange(clamp(v as number))}
        />
      </Box>
      <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
        <TextField
          fullWidth
          size="small"
          type="number"
          label={t('flightLog.litres')}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value === '' ? null : clamp(Number(e.target.value)))}
          slotProps={{
            inputLabel: { shrink: true },
            htmlInput: { min: 0, max, step: 1, inputMode: 'numeric' },
            input: { endAdornment: <InputAdornment position="end">l</InputAdornment> },
          }}
        />
        <TextField
          fullWidth
          size="small"
          type="number"
          label={t('flightLog.usGallons')}
          value={value == null ? '' : Math.round(value / LITRES_PER_USG)}
          onChange={(e) => onChange(e.target.value === '' ? null : clamp(Number(e.target.value) * LITRES_PER_USG))}
          slotProps={{
            inputLabel: { shrink: true },
            htmlInput: { min: 0, max: Math.round(max / LITRES_PER_USG), step: 1, inputMode: 'numeric' },
            input: { endAdornment: <InputAdornment position="end">USG</InputAdornment> },
          }}
        />
      </Stack>
      {usableLitres == null ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {t('flightLog.noTankCapacity')}
        </Typography>
      ) : null}
    </Box>
  )
}

export function Stepper({
  label,
  icon,
  value,
  min,
  max,
  onChange,
}: {
  label: string
  icon: string
  value: number
  min: number
  max: number
  onChange: (value: number) => void
}) {
  return (
    <Box sx={{ textAlign: 'center' }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
        <Icon icon={icon} width={16} /> {label}
      </Typography>
      <Stack direction="row" sx={{ alignItems: 'center' }}>
        <IconButton size="large" aria-label={`${label} −`} disabled={value <= min} onClick={() => onChange(value - 1)}>
          <Icon icon="mdi:minus-circle-outline" width={32} />
        </IconButton>
        <Typography sx={{ fontSize: '1.6rem', fontWeight: 700, minWidth: 40, fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
        <IconButton size="large" aria-label={`${label} +`} disabled={value >= max} onClick={() => onChange(value + 1)}>
          <Icon icon="mdi:plus-circle-outline" width={32} />
        </IconButton>
      </Stack>
    </Box>
  )
}
